import { ampersandList } from "@edgetier/client-components";

import { IVariableToken, IVariableTooltip, VariableTokenKind, VariableTokenSegment } from "./variable-token.types";
import {
    DISALLOWED_VALUE_CHARACTERS_PATTERN,
    KIND_HAS_SUBTYPE,
    RESERVED_VALUE_KEYS,
    VARIABLE_KEY_PATTERN,
    VARIABLE_KINDS,
    VARIABLE_TOKEN_REGEX,
} from "./variable-token.constants";

/** Type guard checking a `type` string against the closed `VARIABLE_KINDS` set. */
const isVariableTokenKind = (kind: string): kind is VariableTokenKind =>
    (VARIABLE_KINDS as readonly string[]).includes(kind);

/**
 * Escapes `\`, `}` and `|` with a leading `\`. A single pass, so no escape is escaped twice.
 * @param text The raw title or label.
 * @returns The text, safe to place between `|` delimiters and before `}}`.
 */
const escapeVariableText = (text: string): string => text.replace(/[\\}|]/g, "\\$&");

/**
 * Splits a token's free-text tail on unescaped `|` and unescapes each part. The regex guarantees a character
 * follows every `\`.
 * @param rawText Capture group 4 of `VARIABLE_TOKEN_REGEX`.
 * @returns The title then the labels, unescaped.
 */
const splitVariableText = (rawText: string): string[] => {
    const parts: string[] = [];
    let current = "";

    for (let index = 0; index < rawText.length; index++) {
        const character = rawText[index];

        if (character === "\\") {
            current += rawText[index + 1];
            index++;
        } else if (character === "|") {
            parts.push(current);
            current = "";
        } else {
            current += character;
        }
    }

    parts.push(current);

    return parts;
};

/**
 * The token grammar's content rules, shared by the serialiser (which throws) and the parser (which leaves the
 * match as text) so the two never disagree.
 * @param variable The candidate variable.
 * @returns The first rule broken, or `null` when the variable is valid.
 */
const findGrammarViolation = ({ type, subtype, values, title, labels }: IVariableToken): string | null => {
    if (!isVariableTokenKind(type)) {
        return `unknown kind "${type}"`;
    }

    if (title.trim() === "") {
        return "empty title";
    }

    if (labels.length === 0) {
        return "no labels";
    }

    if (labels.some((label) => label.trim() === "")) {
        return "empty label";
    }

    const subtypeIsRequired = KIND_HAS_SUBTYPE[type];

    if (subtypeIsRequired && subtype === undefined) {
        return `kind "${type}" requires a subtype`;
    }

    if (subtype !== undefined) {
        if (!subtypeIsRequired) {
            return `kind "${type}" does not support a subtype`;
        }

        if (!VARIABLE_KEY_PATTERN.test(subtype)) {
            return `invalid subtype "${subtype}"`;
        }
    }

    const entries = Object.entries(values);

    if (entries.length === 0) {
        return "no values";
    }

    for (const [key, list] of entries) {
        if (!VARIABLE_KEY_PATTERN.test(key)) {
            return `invalid value key "${key}"`;
        }

        if (RESERVED_VALUE_KEYS.has(key)) {
            return `reserved value key "${key}"`;
        }

        if (list.length === 0) {
            return `no values for key "${key}"`;
        }

        if (new Set(list).size !== list.length) {
            return `duplicate value for key "${key}"`;
        }

        for (const value of list) {
            if (value === "" || value.trim() !== value) {
                return `empty or padded value for key "${key}"`;
            }

            if (DISALLOWED_VALUE_CHARACTERS_PATTERN.test(value)) {
                return `value for key "${key}" contains a reserved character: ")", ";", ",", "=", "{" or "}"`;
            }
        }
    }

    const listEntries = entries.filter(([, list]) => list.length > 1);

    if (listEntries.length > 1) {
        return "more than one key holds multiple values";
    }

    const expectedLabelCount = listEntries.length === 1 ? listEntries[0][1].length : 1;

    if (labels.length !== expectedLabelCount) {
        return `expected ${expectedLabelCount} label(s) but got ${labels.length}`;
    }

    return null;
};

/**
 * Serialises an `IVariableToken` to its canonical form, e.g. `{{agent:(agentId=a1,a2)|Agent|Sarah Chen|John Doe}}`,
 * with no whitespace and everything in insertion order.
 *
 * Throws rather than emit a token that wouldn't round-trip through `parseVariableTokens`. The kind is checked at
 * runtime too, so a stale kind cast from an untyped source (e.g. `JSON.parse`) fails here instead of parsing as text.
 * @param variable The variable to serialise.
 * @returns The token string, ready to be embedded in the outgoing prompt.
 * @throws {Error} Naming the grammar rule the variable breaks.
 */
export const serialiseVariableToken = (variable: IVariableToken): string => {
    const violation = findGrammarViolation(variable);

    if (violation !== null) {
        throw new Error(`Cannot serialise a variable token: ${violation}.`);
    }

    const { type, subtype, values, title, labels } = variable;
    const valueBlock = Object.entries(values)
        .map(([key, list]) => `${key}=${list.join(",")}`)
        .join(";");
    const text = [title, ...labels].map(escapeVariableText).join("|");
    const subtypePart = subtype === undefined ? "" : `#${subtype}`;

    return `{{${type}${subtypePart}:(${valueBlock})|${text}}}`;
};

/**
 * Builds an `IVariableToken` from one `VARIABLE_TOKEN_REGEX` match, or `null` for a near-miss to leave as text.
 * Only a pair with no `=` and a duplicate key are checked here; `findGrammarViolation` checks the rest. Whitespace
 * around `;`, `=` and `,` is trimmed. `Object.fromEntries` defines own properties, so a `__proto__` key can't reach
 * the prototype before it is rejected as reserved.
 * @param type       Capture group 1: the kind, not yet checked against `VARIABLE_KINDS`.
 * @param subtype    Capture group 2: the subtype, or `undefined` when the token has no `#`.
 * @param valueBlock Capture group 3: the raw `;`-separated pairs, untrimmed.
 * @param rawText    Capture group 4: the title and labels, still escaped.
 * @returns The parsed variable, or `null` when the match should be treated as ordinary text.
 */
const tryParseVariable = (
    type: string,
    subtype: string | undefined,
    valueBlock: string,
    rawText: string
): IVariableToken | null => {
    if (!isVariableTokenKind(type)) {
        return null;
    }

    const entries: Array<[string, string[]]> = [];
    const seenKeys = new Set<string>();

    for (const rawPair of valueBlock.split(";")) {
        const equalsIndex = rawPair.indexOf("=");

        if (equalsIndex === -1) {
            return null;
        }

        const key = rawPair.slice(0, equalsIndex).trim();

        if (seenKeys.has(key)) {
            return null;
        }

        seenKeys.add(key);
        entries.push([
            key,
            rawPair
                .slice(equalsIndex + 1)
                .split(",")
                .map((value) => value.trim()),
        ]);
    }

    const [title, ...labels] = splitVariableText(rawText);
    const values = Object.fromEntries(entries);
    const variable: IVariableToken =
        subtype === undefined ? { type, values, title, labels } : { type, subtype, values, title, labels };

    return findGrammarViolation(variable) === null ? variable : null;
};

/**
 * Splits a user-message string into text and variable segments. Each `VARIABLE_TOKEN_REGEX` match becomes a
 * variable segment when `tryParseVariable` accepts it, and is otherwise kept verbatim as text. Adjacent text
 * runs are merged, so the result never holds two consecutive text segments or an empty one; an empty
 * `message` yields `[]`.
 *
 * Iterates a private clone of `VARIABLE_TOKEN_REGEX`: `matchAll` copies the source regex's current
 * `lastIndex`, so a caller that had called `.test()` on the shared export would otherwise make this skip the
 * start of `message`.
 * @param message A user message as persisted, possibly containing variable tokens.
 * @returns The message split into text and variable segments, in source order.
 */
export const parseVariableTokens = (message: string): VariableTokenSegment[] => {
    const segments: VariableTokenSegment[] = [];
    let pendingText = "";

    const flushPendingText = (): void => {
        if (pendingText !== "") {
            segments.push({ type: "text", text: pendingText });
            pendingText = "";
        }
    };

    const tokenRegex = new RegExp(VARIABLE_TOKEN_REGEX.source, VARIABLE_TOKEN_REGEX.flags);
    let cursor = 0;

    for (const match of message.matchAll(tokenRegex)) {
        const [raw, type, subtype, valueBlock, rawText] = match;

        if (match.index === undefined) {
            // matchAll always sets `index`; fail loudly rather than silently duplicate text if that ever changes.
            throw new Error("Variable token match is missing its index.");
        }

        const matchIndex = match.index;

        pendingText += message.slice(cursor, matchIndex);

        const variable = tryParseVariable(type, subtype, valueBlock, rawText);

        if (variable) {
            flushPendingText();
            segments.push({ type: "variable", variable, raw });
        } else {
            pendingText += raw;
        }

        cursor = matchIndex + raw.length;
    }

    pendingText += message.slice(cursor);
    flushPendingText();

    return segments;
};

/**
 * The pill text for a variable: its labels joined like an Explore filter dropdown ("A", "A & B", "A, B & C").
 * Every pill surface uses this so they read the same.
 * @param variable The variable, or anything carrying its labels.
 * @returns The joined labels.
 */
export const formatVariableLabel = ({ labels }: Pick<IVariableToken, "labels">): string => ampersandList([...labels]);

/**
 * The hover tooltip for a variable pill. `items` is set only for several values, since the pill text already shows
 * a single one.
 * @param variable The variable the pill stands for.
 * @param prefix Dimmed kind name before the title, e.g. "Agent Groups" or "AI Tags". Omitted when empty.
 * @returns The tooltip's prefix, title and items.
 */
export const getVariableTooltip = (variable: IVariableToken, prefix?: string): IVariableTooltip => ({
    ...(prefix ? { prefix } : {}),
    title: variable.title,
    ...(variable.labels.length > 1 ? { items: variable.labels } : {}),
});

/**
 * Projects a variable-token message to plain text for contexts that can't render pills (e.g. conversation titles),
 * collapsing each variable to its pill text.
 * @param message A user message as persisted, possibly containing variable tokens.
 * @returns The message with every token replaced by its joined labels.
 */
export const variableTokensToPlainText = (message: string): string =>
    parseVariableTokens(message)
        .map((segment) => (segment.type === "text" ? segment.text : formatVariableLabel(segment.variable)))
        .join("");
